import React, { useState, useContext } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Business,
  School,
  People,
  Assignment,
  Settings,
  Logout,
  ExpandLess,
  ExpandMore,
  Computer,
  LightMode,
  DarkMode,
  AccountCircle,
  Category,
  BookOnline,
  QuestionAnswer,
  HourglassEmpty,
  CancelPresentation,
  PersonAdd,
  HowToReg,
  CheckCircle,
  Cancel,
  Store,
  Payment,
  Send,
  CalendarMonth,
  MarkEmailRead,
  Print,
} from '@mui/icons-material';

import { AuthContext } from '../contexts/AuthContext';
import { ThemeContext } from '../contexts/ThemeContext';
import { toast } from 'react-toastify';
import BlockedBranchDialog from '../components/BlockedBranchDialog';

const drawerWidth = 260;

const AdminLayout = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { user, logout, isSuperAdmin, isBranchAdmin } = useContext(AuthContext);
  const { mode, toggleTheme } = useContext(ThemeContext);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [coursesOpen, setCoursesOpen] = useState(false);
  const [studentsOpen, setStudentsOpen] = useState(false);
  const [examsOpen, setExamsOpen] = useState(false);

  // Redirect students to their dashboard
  React.useEffect(() => {
    if (user && user.role === 'student' && (location.pathname === '/admin' || location.pathname === '/admin/dashboard')) {
      navigate('/student/dashboard', { replace: true });
    }
  }, [user, location.pathname, navigate]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  // Menu items based on user role
  // Roles: superadmin (level 1), branchadmin (level 5), student (level 10+)
  const menuItems = [
    {
      text: 'Dashboard',
      icon: <Dashboard />,
      path: '/admin',
      roles: ['superadmin', 'branchadmin'],
      minLevel: 1 // Only admins can access
    },
    {
      text: 'Branches',
      icon: <Business />,
      path: '/admin/branches',
      roles: ['superadmin'],
      minLevel: 1 // Only Super Admin (level 1)
    },
    {
      text: 'Courses',
      icon: <School />,
      path: '/admin/courses',
      roles: ['superadmin', 'branchadmin'],
      minLevel: 1,
      submenu: true,
      items: [
        { text: 'All Courses', path: '/admin/courses', icon: <BookOnline />, roles: ['superadmin', 'branchadmin'] },
        { text: 'Categories', path: '/admin/courses/categories', icon: <Category />, roles: ['superadmin'] },
      ],
    },
    {
      text: 'Students',
      icon: <People />,
      path: '/admin/students',
      roles: ['superadmin', 'branchadmin'],
      minLevel: 1,
      submenu: true,
      items: [
        { text: 'All Students', path: '/admin/students', icon: <People />, roles: ['superadmin', 'branchadmin'] },
        { text: 'Submit for Approval', path: '/admin/students/submit', icon: <Send />, roles: ['branchadmin'] },
        { text: 'Pending Approvals', path: '/admin/students/pending', icon: <HourglassEmpty />, roles: ['superadmin', 'branchadmin'] },
        { text: 'Rejected', path: '/admin/students/rejected', icon: <CancelPresentation />, roles: ['superadmin', 'branchadmin'] },
        { text: 'Alumni', path: '/admin/students/alumni', icon: <School />, roles: ['superadmin', 'branchadmin'] },
      ],
    },
    {
      text: 'Batch Setting',
      icon: <CalendarMonth />,
      path: '/admin/batches',
      roles: ['superadmin'],
      minLevel: 1
    },
    {
      text: 'Franchise Enquiries',
      icon: <Store />,
      path: '/admin/franchise-enquiries',
      roles: ['superadmin'],
      minLevel: 1
    },
    {
      text: 'Consultation Requests',
      icon: <QuestionAnswer />,
      path: '/admin/consultation-requests',
      roles: ['superadmin'],
      minLevel: 1
    },
    {
      text: 'Payments',
      icon: <Payment />,
      path: '/admin/payments',
      roles: ['superadmin', 'branchadmin'],
      minLevel: 1
    },
    {
      text: 'Examinations',
      icon: <Assignment />,
      path: '/admin/examinations',
      roles: ['all'],
      minLevel: 1,
      submenu: true,
      items: [
        { text: 'All Exams', path: '/admin/examinations', icon: <Assignment />, roles: ['superadmin', 'branchadmin'] },
        { text: 'Questions', path: '/admin/examinations/questions', icon: <QuestionAnswer />, roles: ['superadmin'] },
        { text: 'Assign Students', path: '/admin/examinations/assign-students', icon: <PersonAdd />, roles: ['branchadmin'] },
        { text: 'Pending Approvals', path: '/admin/examinations/pending-approvals', icon: <HowToReg />, roles: ['superadmin'] },
        { text: 'Approved Students', path: '/admin/examinations/approved-students', icon: <CheckCircle />, roles: ['superadmin', 'branchadmin'] },
        { text: 'Rejected Students', path: '/admin/examinations/rejected-students', icon: <Cancel />, roles: ['superadmin', 'branchadmin'] },
        { text: 'Exam Results', path: '/admin/examinations/results', icon: <Assignment />, roles: ['superadmin', 'branchadmin'] },
        { text: 'Centerwise Print', path: '/admin/examinations/centerwise-print', icon: <Print />, roles: ['superadmin'] },
        { text: 'My Exams', path: '/student/dashboard', icon: <Assignment />, roles: ['student'] },
        { text: 'My Results', path: '/student/results', icon: <CheckCircle />, roles: ['student'] },
      ],
    },
    {
      text: 'Email Reset',
      icon: <MarkEmailRead />,
      path: '/admin/email-reset',
      roles: ['superadmin'],
      minLevel: 1
    },
    {
      text: 'Settings',
      icon: <Settings />,
      path: '/admin/settings',
      roles: ['all'],
      minLevel: 1
    },
  ];

  const filterMenuByRole = (items) => {
    return items.filter((item) => {
      // Items with 'all' role are accessible to everyone
      if (item.roles.includes('all')) {
        return true;
      }

      // Check specific role-based access
      if (isSuperAdmin && item.roles.includes('superadmin')) return true;
      if (isBranchAdmin && item.roles.includes('branchadmin')) return true;
      if (user?.role === 'student' && item.roles.includes('student')) return true;

      return false;
    });
  };

  const filterSubmenuByRole = (items) => {
    if (!items) return [];
    return items.filter((item) => {
      if (!item.roles) return true;
      if (item.roles.includes('all')) return true;
      if (isSuperAdmin && item.roles.includes('superadmin')) return true;
      if (isBranchAdmin && item.roles.includes('branchadmin')) return true;
      if (user?.role === 'student' && item.roles.includes('student')) return true;
      return false;
    });
  };

  const getUserRoleLabel = () => {
    if (isSuperAdmin) return 'Super Admin';
    if (isBranchAdmin) return 'Branch Admin';
    if (user?.role === 'student') return 'Student';
    return user?.role || 'User';
  };

  const drawer = (
    <Box>
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: [1],
          py: 3,
          background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',
        }}
      >
        <Computer sx={{ fontSize: 32, color: 'white', mr: 1 }} />
        <Typography variant="h6" noWrap component="div" sx={{ color: 'white', fontWeight: 800 }}>
          BSS EDU-ERP
        </Typography>
      </Toolbar>
      <Box
        sx={{
          px: 2,
          py: 2,
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
            {user?.name?.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
              {getUserRoleLabel()}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Divider />
      <List sx={{ px: 2, py: 1 }}>
        {filterMenuByRole(menuItems).map((item) => (
          <React.Fragment key={item.text}>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => {
                  if (item.submenu) {
                    if (item.text === 'Courses') {
                      setCoursesOpen(!coursesOpen);
                    } else if (item.text === 'Students') {
                      setStudentsOpen(!studentsOpen);
                    } else if (item.text === 'Examinations') {
                      setExamsOpen(!examsOpen);
                    }
                  } else {
                    navigate(item.path);
                    if (isMobile) setMobileOpen(false);
                  }
                }}
                sx={{
                  borderRadius: 2,
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: location.pathname === item.path ? 'inherit' : 'text.secondary',
                    minWidth: 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
                {item.submenu && (
                  <>
                    {item.text === 'Courses' ? (
                      coursesOpen ? <ExpandLess /> : <ExpandMore />
                    ) : item.text === 'Students' ? (
                      studentsOpen ? <ExpandLess /> : <ExpandMore />
                    ) : item.text === 'Examinations' ? (
                      examsOpen ? <ExpandLess /> : <ExpandMore />
                    ) : (
                      <ExpandMore />
                    )}
                  </>
                )}
              </ListItemButton>
            </ListItem>

            {item.submenu && (
              <Collapse
                in={
                  item.text === 'Courses'
                    ? coursesOpen
                    : item.text === 'Students'
                    ? studentsOpen
                    : item.text === 'Examinations'
                    ? examsOpen
                    : false
                }
                timeout="auto"
                unmountOnExit
              >
                <List component="div" disablePadding>
                  {filterSubmenuByRole(item.items).map((subItem) => (
                    <ListItemButton
                      key={subItem.text}
                      sx={{ pl: 4, borderRadius: 2, mb: 0.5 }}
                      selected={location.pathname === subItem.path}
                      onClick={() => {
                        navigate(subItem.path);
                        if (isMobile) setMobileOpen(false);
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>{subItem.icon}</ListItemIcon>
                      <ListItemText primary={subItem.text} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', overflow: 'hidden', minHeight: '100vh' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: theme.palette.mode === 'dark' ? '#1F2937' : 'white',
          color: 'text.primary',
          boxShadow: 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            {menuItems.find((item) => item.path === location.pathname)?.text || 'Dashboard'}
          </Typography>

          <IconButton onClick={toggleTheme} color="inherit" sx={{ mr: 1 }}>
            {mode === 'light' ? <DarkMode /> : <LightMode />}
          </IconButton>

          <IconButton onClick={handleProfileMenuOpen} sx={{ p: 0 }}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              {user?.name?.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem disabled>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {user?.name}
              </Typography>
            </MenuItem>
            <MenuItem disabled>
              <Typography variant="caption" color="text.secondary">
                {user?.email}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                handleMenuClose();
                navigate('/admin/settings');
              }}
            >
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleMenuClose();
                navigate('/admin/settings');
              }}
            >
              <ListItemIcon>
                <Settings fontSize="small" />
              </ListItemIcon>
              Settings
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              overflowX: 'hidden',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minWidth: 0,
          mt: 8,
          minHeight: '100vh',
          bgcolor: 'background.default',
          overflow: 'auto',
        }}
      >
        <Outlet />
      </Box>

      {/* Blocked Branch Dialog */}
      <BlockedBranchDialog />
    </Box>
  );
};

export default AdminLayout;
